import React, {FC} from 'react';
import classNames from 'classnames';
import {CloseIcon} from 'components';
import {FormSelect} from 'components/Basic/Form/Select/FormSelect';
import {detachedSearchBarStyles} from 'components/Basic/Form/Select/styles';
import {statesAbbreviation} from 'definitions/States';
import {selectSlotAvailabilityStatus} from 'features/Appointment';
import {BookingCalendar} from 'features/Appointment/Booking/BookAppointment/BookingCalendar';
import {TimeGrid} from 'features/Appointment/Booking/BookAppointment/TimeGrid';
import {useBreakpoint, useLocale, useRequesting} from 'hooks';
import {SliceStatus} from 'interfaces';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router';
import styled from 'styled-components';

import {
  getDirectBookingSelectedProvider,
  selectProvider,
} from '../directBookingSelectors';
import {useDirectBookingFilter} from '../useDirectBookingFilter';

import {DirectBookingSubmitButton} from './DirectBookingSubmitButton';
import {useDirectBookingAvailability} from './useDirectBookingAvailability';

export const BookingCalendarContainer = styled.section`
  .DayPicker-Caption > div {
    text-align: center;
  }

  &&&& .DayPicker-Day {
    border-radius: 15px;
    outline: ${props => `2px solid ${props.theme.colors.gray[200]}`};
    outline-offset: -4px;
    font-size: 14px;
    @media (max-width: 400px) {
      padding: 0.8rem;
      border-radius: 12px;
    }
    @media (max-width: 350px) {
      font-size: 12px;
      border-radius: 10px;
    }
    @media (max-width: 300px) {
      padding: 0.5rem;
    }
  }

  &&&& .DayPicker-Day--disabled {
    outline: none;
  }

  &&&& .DayPicker-Day--selected {
    outline: 3px solid white;
    outline-offset: -2px;
    font-weight: normal;
  }
`;

type Props = {
  isModal: boolean;
  onCloseClick: () => void;
  filter: ReturnType<typeof useDirectBookingFilter>;
  availability: ReturnType<typeof useDirectBookingAvailability>;
};

export const DirectBookingAvailability: FC<Props> = ({
  isModal,
  onCloseClick,
  filter,
  availability,
}: Props) => {
  const {isNotXs: isMobileScreen} = useBreakpoint('xs');
  const isProfileLoading =
    useRequesting('directBooking') === SliceStatus.pending;
  const isSlotLoading =
    useSelector(selectSlotAvailabilityStatus) === SliceStatus.pending;

  const {
    disabledDays,
    control,
    watch,
    time,
    selectedTime,
    errors,
    register,
    getProviderCalendarData,
    state,
    timezone,
  } = availability;

  const {userId} = useParams<{userId: string}>();
  const selectedProvider = useSelector(getDirectBookingSelectedProvider);
  const providerById = useSelector(selectProvider(userId));
  const provider = selectedProvider ?? providerById;

  const providerStateOptions =
    selectedProvider?.state?.map(
      s =>
        statesAbbreviation.find(sa => sa.value === s) ?? {
          label: s,
          value: s,
        },
    ) ?? [];

  const {locale} = useLocale();
  const isInternationalUser = locale !== 'en-us';
  // Covering edge case when user has two tabs with different countries
  const providerCurrentCountry = provider?.country;
  const isInternationalProvider =
    providerCurrentCountry && providerCurrentCountry !== 'United States';

  const {t} = useTranslation();
  return (
    <section
      className={classNames(
        'bg-white rounded-xl p-6 shadow text-left flex flex-col',
        {
          'fixed inset-0 z-10 overflow-y-scroll': isModal,
        },
      )}
    >
      <div className="flex items-center">
        <h2 className="font-semibold float-left">
          {t('bookSession', 'Book Session')}
        </h2>
        {isModal ? (
          <button
            className="focus:outline-none ml-auto bg-gray-200 rounded-full w-8 h-8"
            onClick={onCloseClick}
            key="closeButton"
          >
            <CloseIcon
              width={25}
              height={25}
              strokeColor="text-gray-700 m-auto group-hover:text-gray-700"
              strokeWidth={33}
            />
          </button>
        ) : null}
      </div>

      <div
        className={classNames('flex flex-col max-w-[437px] m-auto', {
          'pb-[10vh]': isModal,
        })}
      >
        {!isInternationalUser && !isInternationalProvider ? (
          <FormSelect
            control={filter.control}
            id="paymentType"
            label="Payment type"
            labelClasses="block font-normal text-xs leading-snug mb-2"
            inputClasses="text-sm"
            extractValue={true}
            options={filter.paymentTypeOptions}
            isLoading={filter.isLoadingOptions}
            placeholder="your insurance"
            isSearchable={true}
            fullScreen={isMobileScreen}
            styles={{
              ...detachedSearchBarStyles,
              detachedButton: (isOpen: boolean) => ({
                ...detachedSearchBarStyles.detachedButton!(isOpen, false),
                backgroundColor: 'rgb(230 230 230 / 0.2)',
              }),
            }}
            classes="inline-block text-sm text-left"
            detachedSearchBar={true}
          />
        ) : null}

        {selectedProvider?.profession === 'Prescriber' ? (
          <FormSelect
            control={filter.control}
            required={true}
            id="state"
            label="State"
            labelClasses="block font-normal text-xs leading-snug mb-2"
            inputClasses="text-sm"
            extractValue={true}
            options={providerStateOptions}
            placeholder="your state"
            isSearchable={true}
            fullScreen={isMobileScreen}
            styles={{
              ...detachedSearchBarStyles,
              detachedButton: (isOpen: boolean) => ({
                ...detachedSearchBarStyles.detachedButton!(isOpen, false),
                backgroundColor: 'rgb(230 230 230 / 0.2)',
              }),
            }}
            classes="inline-block text-sm text-left"
            detachedSearchBar={true}
          />
        ) : null}

        {selectedProvider?.profession === 'Prescriber' && !state ? null : (
          <BookingCalendarContainer className="mt-7">
            <label className="font-normal text-center text-xs">
              {t('selectDateAndTime', 'Select Date & Time')}
            </label>
            <BookingCalendar
              isAvailabilityLoading={isProfileLoading || isSlotLoading}
              disabledDays={disabledDays}
              control={control}
              onMonthChange={getProviderCalendarData}
            ></BookingCalendar>
          </BookingCalendarContainer>
        )}

        <p className="capitalize text-xs font-light mt-3 text-center">
          Time Zone &bull; {timezone}
        </p>
        {isProfileLoading || isSlotLoading ? null : (
          <TimeGrid
            time={time}
            watch={watch}
            register={register}
            selectedTime={selectedTime}
            errors={errors}
            unavailableText={
              !filter.selectedOptions?.state &&
              selectedProvider?.profession === 'Prescriber'
                ? 'Please select state to view available times.'
                : undefined
            }
          ></TimeGrid>
        )}
      </div>

      <div className="basis-[100%]" />

      {!isModal ? (
        <DirectBookingSubmitButton
          filter={filter}
          availability={availability}
          providerId={provider.providerId}
        />
      ) : null}
    </section>
  );
};
